import React, { useState } from "react";
import { Button, Card, Collapse, Input } from "antd";
import {
  UploadCloud,
  Sparkles,
  CheckCircle,
  Wallet,
  IndianRupee,
  ImageIcon,
  Clock,
  Zap,
  ShieldCheck,
  Lock,
  RotateCcw,
  Heart,
  Headphones,
  Users,
} from "lucide-react";
import WebsiteHeader from "../Components/WebsiteHeader";
import WebsiteFooter from "../Components/WebsiteFooter";

const quickAmounts = [100, 300, 500, 1000, 2000];

export default function Pricing() {
  const [amount, setAmount] = useState(500);

   const handleRedirect = (url) => {
        // Sends the user to the specified external website URL
        window.location.href = url;
    };

  return (
    <div className="bg-[#fbfaff] min-h-screen text-slate-900">
      <WebsiteHeader />

      <main className="max-w-[1200px] mx-auto px-4 py-12">
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full font-bold text-sm mb-6">
              ✨ Simple, Transparent & Fair
            </div>

            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
              Simple Credit System <br />
              Pay Only For What{" "}
              <span className="text-purple-600">You Use</span>
            </h1>

            <p className="text-lg text-slate-600 mt-5">
              No monthly fees. No hidden charges. <br />
              Just add credits and start optimizing.
            </p>

            <div className="mt-8 bg-white border rounded-2xl shadow-sm p-5 inline-flex items-center gap-5">
              <div className="w-14 h-14 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                <Wallet size={30} />
              </div>
              <h2 className="text-3xl font-extrabold m-0">
                1 Credit = <span className="text-purple-600">₹1</span>
              </h2>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-extrabold text-center mb-5">
              Optimize More, Save More
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-center">
              <FlowCard
                icon={<UploadCloud />}
                title="Upload Image"
                desc="Upload your product image"
              />
              <FlowCard
                icon={<Sparkles />}
                title="AI Optimizes"
                desc="Our AI finds the lowest shipping charge"
              />
              <FlowCard
                icon={<CheckCircle />}
                title="You Save"
                desc="Download optimized image and save more"
              />
            </div>

            <div className="mt-6 bg-purple-50 rounded-xl p-4 text-center text-purple-700 font-extrabold text-lg">
              Lower Shipping Charges = More Orders = More Sales 📈
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-[430px_1fr_360px] gap-6 mt-10">
          <Card className="rounded-2xl shadow-sm border-purple-200">
            <div className="flex items-center gap-4 mb-6">
              <IconBox icon={<Wallet />} color="purple" />
              <div>
                <h2 className="text-xl font-extrabold m-0">Add Credits</h2>
                <p className="text-slate-500 m-0">
                  Enter amount and get credits instantly
                </p>
              </div>
            </div>

            <label className="font-bold block mb-2">Enter Amount (₹)</label>
            <Input
              size="large"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
              prefix={<span className="font-bold text-xl">₹</span>}
              className="rounded-xl h-14 text-xl font-bold"
            />

            <div className="text-center my-6">
              <p className="font-bold m-0">You will receive</p>
              <h2 className="text-3xl font-extrabold text-purple-600 my-2">
                {amount || 0} Credits
              </h2>
              <p className="text-green-600 font-semibold m-0">
                ● Credits never expire
              </p>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {quickAmounts.map((item) => (
                <button
                  key={item}
                  onClick={() => setAmount(item)}
                  className={`h-12 rounded-lg border font-bold ${
                    Number(amount) === item
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-white border-slate-200"
                  }`}
                >
                  ₹{item}
                </button>
              ))}
            </div>

            <Button
              type="primary"
              block
              size="large"
              className="mt-6 h-14 bg-purple-600 font-bold rounded-xl"
            >
              Add ₹{amount || 0} & Get {amount || 0} Credits
            </Button>

            <p className="text-center text-slate-500 mt-5 text-sm">
              🔒 Secure payments powered by Cashfree
            </p>
          </Card>

          <Card className="rounded-2xl shadow-sm">
            <h2 className="text-xl font-extrabold mb-6">
              How Credits Work
            </h2>

            <CreditRow
              icon={<IndianRupee />}
              title="1 Credit = ₹1"
              desc="Every ₹1 you add = 1 Credit"
              color="purple"
            />
            <CreditRow
              icon={<ImageIcon />}
              title="1 Optimization = 10 Credits"
              desc="Each image optimization costs 10 credits"
              color="orange"
            />
            <CreditRow
              icon={<Clock />}
              title="Credits Never Expire"
              desc="Use your credits anytime, at your pace"
              color="green"
            />
            <CreditRow
              icon={<Zap />}
              title="Use Anytime"
              desc="No daily or monthly limits"
              color="blue"
            />
          </Card>

          <Card className="rounded-2xl shadow-sm bg-green-50 border-green-200">
            <h2 className="text-xl font-extrabold mb-7">Example</h2>

            <ExampleRow label="You Add" value="₹300" />
            <ExampleRow label="You Get" value="300 Credits" />
            <ExampleRow label="1 Optimization" value="10 Credits" dark />

            <div className="mt-5">
              <p className="text-slate-600 m-0">So, You Can Do</p>
              <h2 className="text-2xl font-extrabold">
                300 ÷ 10 ={" "}
                <span className="text-green-600">30 Optimizations</span>
              </h2>
            </div>

            <div className="mt-6 bg-green-100 rounded-xl p-4 font-bold text-green-800">
              💡 More optimizations = More savings on shipping charges!
            </div>
          </Card>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white rounded-2xl shadow-sm border p-5 mt-8">
          <TrustItem icon={<ShieldCheck />} title="100% Secure Payments" desc="Your payment and data are safe with us." />
          <TrustItem icon={<Lock />} title="No Hidden Charges" desc="Pay only for credits. No extra fees." />
          <TrustItem icon={<RotateCcw />} title="Instant Credit" desc="Credits added instantly to your wallet." />
          <TrustItem icon={<Heart />} title="Built for Meesho Sellers" desc="Made by a seller, for sellers." />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-[1fr_430px] gap-6 mt-8">
          <Card className="rounded-2xl shadow-sm">
            <h2 className="text-xl font-extrabold text-purple-600 mb-4">
              Frequently Asked Questions
            </h2>

            <Collapse
              bordered={false}
              items={[
                {
                  key: "1",
                  label: "Do credits expire?",
                  children: "No, your credits never expire.",
                },
                {
                  key: "2",
                  label: "How many credits are required for one optimization?",
                  children: "Each image optimization costs 10 credits.",
                },
                {
                  key: "3",
                  label: "Can I add more credits later?",
                  children: "Yes, you can add credits anytime.",
                },
                {
                  key: "4",
                  label: "What is the minimum amount I can add?",
                  children: "Minimum recharge amount is ₹50.",
                },
                {
                  key: "5",
                  label: "Is my payment secure?",
                  children: "Yes, payments are secure and encrypted.",
                },
              ]}
            />
          </Card>

          <div className="space-y-6">
            {/* <Card className="rounded-2xl shadow-sm bg-purple-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-extrabold">Need Help?</h2>
                  <p className="text-slate-600">
                    Our team is here to help you.
                  </p>
                  <Button onClick={() => handleRedirect('https://wa.me/918602490161?text=Hi Shipless Tool')} className="border-purple-600 text-purple-600 font-bold">
                    Chat on WhatsApp
                  </Button>
                </div>
                <Headphones size={90} className="text-purple-600" />
              </div>
            </Card> */}

            <Card className="rounded-2xl shadow-sm">
              <div className="flex items-center gap-4">
                <IconBox icon={<Users />} color="purple" />
                <div>
                  <h3 className="font-bold m-0">Trusted by Beta Sellers</h3>
                  <p className="text-slate-500 m-0 mt-1">
                    Join our growing community of smart Meesho sellers.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </main>

      <WebsiteFooter/>
    </div>
  );
}

function FlowCard({ icon, title, desc }) {
  return (
    <Card className="rounded-2xl text-center shadow-sm h-full">
      <div className="w-16 h-16 mx-auto rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
        {icon}
      </div>
      <h3 className="font-bold mt-4">{title}</h3>
      <p className="text-slate-500 m-0">{desc}</p>
    </Card>
  );
}

function IconBox({ icon, color }) {
  const map = {
    purple: "bg-purple-100 text-purple-600",
    green: "bg-green-100 text-green-600",
    orange: "bg-orange-100 text-orange-600",
    blue: "bg-blue-100 text-blue-600",
  };

  return (
    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${map[color]}`}>
      {icon}
    </div>
  );
}

function CreditRow({ icon, title, desc, color }) {
  return (
    <div className="flex items-center gap-5 mb-7 last:mb-0">
      <IconBox icon={icon} color={color} />
      <div>
        <h3 className="font-extrabold m-0">{title}</h3>
        <p className="text-slate-500 m-0 mt-1">{desc}</p>
      </div>
    </div>
  );
}

function ExampleRow({ label, value, dark }) {
  return (
    <div className="border-b border-green-200 pb-4 mb-4">
      <p className="text-slate-600 m-0">{label}</p>
      <h3 className={`font-extrabold text-xl m-0 ${dark ? "" : "text-green-600"}`}>
        {value}
      </h3>
    </div>
  );
}

function TrustItem({ icon, title, desc }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-14 h-14 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h3 className="font-bold m-0">{title}</h3>
        <p className="text-slate-500 m-0 text-sm">{desc}</p>
      </div>
    </div>
  );
}